import { orgAPI } from "@/api/api";
import { fileRecordKeys } from "@/api/fileRecords";
import { patchWorkspaceFlowSessionNameInLists } from "@/api/flow_sessions";
import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { EventStreamEvent, FlowSessionEventData } from "@/types/events";
import { parsePartialJson } from "@/utils/parsePartialJson";
import {
  Canvas,
  FileRecord,
  InboxItem,
  MeData,
  Message,
  MessagesResponse,
  ToolCall,
  didCanvasHeroImagesChange,
} from "@cohesive/api-client";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { useCallback } from "react";
import { matchPath, useLocation } from "react-router-dom";

declare module "@cohesive/api-client" {
  interface ToolCall {
    status?: "processing" | "done";
    // Streaming-args fast path state (mid-stream only — both fields are
    // cleared once `flow_message.tool_call` lands with the full args).
    //   _accumulatedArgs: raw concatenated args_delta chunks
    //   _argsParsedDone:  true once we've extracted `action_description`,
    //                     at which point further deltas no-op for parsing
    _accumulatedArgs?: string;
    _argsParsedDone?: boolean;
  }

  interface Message {
    // Streaming dedup state (mid-stream only): the (attempt, index) of the
    // last delta applied per rail. Backend activity retries republish the
    // whole stream under the same message id; tracking the position lets a
    // restarted stream replace the accumulated text instead of appending a
    // duplicate copy.
    _lastContentDelta?: StreamDeltaPosition;
    _lastThinkingDelta?: StreamDeltaPosition;
  }
}

interface StreamDeltaPosition {
  attempt: number;
  index: number;
}

// Classifies a streamed delta against the last position applied to the
// message: "append" for in-order chunks, "restart" when a newer backend
// attempt took over (its content replaces what the aborted attempt
// streamed), "drop" for stale chunks — duplicates, and in-flight stragglers
// from an aborted attempt that race past the restart. Deltas from backends
// that predate attempt stamping fall back to treating index 0 as a restart.
function classifyDelta(
  last: StreamDeltaPosition | undefined,
  attempt: number,
  index: number,
): "append" | "restart" | "drop" {
  if (last == null) {
    return "append";
  }
  if (attempt > last.attempt) {
    return "restart";
  }
  if (attempt < last.attempt) {
    return "drop";
  }
  if (index > last.index) {
    return "append";
  }
  return index === 0 && last.index > 0 ? "restart" : "drop";
}

function mergeCanvasQueryEntry(prev: Canvas | undefined, next: Canvas): Canvas {
  if (!prev) {
    return next;
  }
  const nextMeta = next.metadata;
  const metadata: Canvas["metadata"] = {
    ...prev.metadata,
    ...nextMeta,
  };
  if (
    nextMeta != null &&
    "hero_generating" in nextMeta &&
    nextMeta.hero_generating !== undefined
  ) {
    metadata.hero_generating = nextMeta.hero_generating;
  } else if (didCanvasHeroImagesChange(prev, next)) {
    // Both themed hero URLs must change before we treat capture as done —
    // light and dark can land in separate `canvas.updated` events, and
    // clearing on the first would drop the generating spinner early. URL
    // diffs (not mere re-presign echoes of the same capture) gate this so
    // unrelated updates that repeat stale hero URLs don't clear the flag.
    metadata.hero_generating = false;
  }
  return {
    ...prev,
    ...next,
    metadata,
  };
}

export function useHandleWebSocketEvent() {
  const queryClient = useQueryClient();
  const { data: user } = useUserInfo();
  const location = useLocation();

  return useCallback(
    (event: EventStreamEvent) => {
      // Determine which session is actively being viewed
      // Check main session page
      const mainSessionMatch = matchPath(
        ROUTES.SESSIONS.SESSION,
        location.pathname,
      );

      // Check right pane routes
      const paneSessionMatch = matchPath(
        ROUTES.SESSIONS.SESSION,
        location.pathname,
      );

      const paneInboxMatch = matchPath(ROUTES.INBOX.ITEM, location.pathname);

      // Extract session ID from direct session routes
      const activeSessionId =
        mainSessionMatch?.params.sessionId ||
        paneSessionMatch?.params.sessionId;

      // For inbox items, resolve to session ID from cache
      const activeInboxItemId = paneInboxMatch?.params.inboxItemId;
      const cachedInboxItem = activeInboxItemId
        ? queryClient.getQueryData<{ flow_session_id: string }>([
            "inboxItem",
            activeInboxItemId,
          ])
        : null;
      const sessionIdFromInbox = cachedInboxItem?.flow_session_id;

      // Combined active session ID
      const viewedSessionId = activeSessionId || sessionIdFromInbox;

      switch (event.type) {
        case "flow_session.created":
          // TODO - Remove when https://github.com/venchrz/backend/issues/836 is resolved
          const flowSessionId = event.data.flow_session.id;

          queryClient.fetchQuery({
            queryKey: [`flowSession`, flowSessionId],
            queryFn: async () => {
              const response = await orgAPI.flowSessions.getById(flowSessionId);
              return response.data.flow_session;
            },
          });

          break;

        case "flow_session.updated": {
          const updatedSession = event.data.flow_session;

          // Moving to trash arrives as flow_session.updated with archived_at
          // set. Mirror flow_session.deleted: drop the detail cache so a
          // navigated-in view refetches the in-trash state, and refresh lists +
          // trash. Gated on archived_at so routine status updates during a chat
          // (which fire this event frequently) don't thrash the lists.
          if (updatedSession.archived_at) {
            queryClient.removeQueries({
              queryKey: ["flowSession", String(updatedSession.id)],
            });
            queryClient.invalidateQueries({
              queryKey: ["workspace-flow-sessions"],
            });
            queryClient.invalidateQueries({
              queryKey: ["canvas-flow-sessions"],
            });
            queryClient.invalidateQueries({ queryKey: ["archived"] });
            break;
          }

          // Restore (archived_at cleared) reaches the owner's other clients as
          // a plain flow_session.updated — the chat was dropped from caches
          // when archived, so if its detail isn't cached yet refresh the lists
          // to bring it back. Gated on detail presence (and these events are
          // per-user) so the hot path — status updates for the chat being
          // viewed, which is cached — doesn't thrash the lists. The setQueryData
          // below seeds the detail on first sight, so this fires at most once.
          if (
            !queryClient.getQueryData([
              "flowSession",
              String(updatedSession.id),
            ])
          ) {
            queryClient.invalidateQueries({
              queryKey: ["workspace-flow-sessions"],
            });
            queryClient.invalidateQueries({
              queryKey: ["canvas-flow-sessions"],
            });
            queryClient.invalidateQueries({ queryKey: ["archived"] });
          }

          const flowSessionId = String(updatedSession.id);
          const cachedSession = queryClient.getQueryData<
            FlowSessionEventData["flow_session"]
          >(["flowSession", flowSessionId]);
          const nameChanged =
            updatedSession.name !== undefined &&
            cachedSession?.name !== updatedSession.name;

          // Update individual session cache
          queryClient.setQueryData<FlowSessionEventData["flow_session"]>(
            ["flowSession", flowSessionId],
            (oldData: FlowSessionEventData["flow_session"] | undefined) =>
              oldData
                ? {
                    ...oldData,
                    ...updatedSession,
                    typewrite: oldData.name !== updatedSession.name,
                  }
                : updatedSession,
          );

          // Mirror canvas.updated: patch inbox-backed chat lists in place on
          // rename so ChatsRail stays in sync with the tab bar without
          // invalidating the whole list on every status tick.
          if (nameChanged) {
            patchWorkspaceFlowSessionNameInLists(
              queryClient,
              flowSessionId,
              updatedSession.name,
            );
          }

          break;
        }

        case "flow_message.created":
        case "flow_message.updated":
          const updatedMessage = event.data.flow_message;

          if (updatedMessage.origin === "assistant-tool-call") {
            updatedMessage.tool_calls = updatedMessage.tool_calls?.map(
              (toolCall) => {
                toolCall.status = "processing";
                return toolCall;
              },
            );
          }

          if (updatedMessage.origin === "tool") {
            queryClient.setQueryData<InfiniteData<MessagesResponse>>(
              ["flowMessages", updatedMessage.flow_session_id],
              (oldData: InfiniteData<MessagesResponse> | undefined) => {
                if (!oldData) return oldData;

                const newData = cloneDeep(oldData);

                for (const page of newData.pages) {
                  for (const message of page.data.flow_messages) {
                    if (message.tool_calls?.length) {
                      for (const toolCall of message.tool_calls) {
                        if (toolCall.id === updatedMessage.tool_call_id) {
                          toolCall.status = "done";
                        }
                      }
                    }
                  }
                }

                return newData;
              },
            );
          }

          queryClient.setQueryData<InfiniteData<MessagesResponse>>(
            ["flowMessages", updatedMessage.flow_session_id],
            (oldData: InfiniteData<MessagesResponse> | undefined) => {
              if (!oldData) return oldData;

              const newData = cloneDeep(oldData);

              if (updatedMessage.origin === "tool") {
                for (const page of newData.pages) {
                  for (const message of page.data.flow_messages) {
                    if (message.tool_calls?.length) {
                      for (const toolCall of message.tool_calls) {
                        if (toolCall.id === updatedMessage.tool_call_id) {
                          toolCall.status = "done";
                        }
                      }
                    }
                  }
                }
              }

              let found = false;
              // Search through all pages to find and replace the message
              for (const page of newData.pages) {
                const index = page.data.flow_messages.findIndex(
                  (msg) => msg.id === updatedMessage.id,
                );
                if (index !== -1) {
                  const existingMessage = page.data.flow_messages[index];
                  const merged = { ...updatedMessage };
                  // Server payloads never carry the FE-only stream dedup
                  // state; keep it across the replace so late or replayed
                  // deltas still classify correctly after an update.
                  merged._lastContentDelta = existingMessage._lastContentDelta;
                  merged._lastThinkingDelta =
                    existingMessage._lastThinkingDelta;
                  if (
                    (merged.thinking_content == null ||
                      merged.thinking_content === "") &&
                    existingMessage.thinking_content != null &&
                    existingMessage.thinking_content !== ""
                  ) {
                    merged.thinking_content = existingMessage.thinking_content;
                    merged.thinking_duration_milliseconds =
                      existingMessage.thinking_duration_milliseconds;
                  }
                  page.data.flow_messages[index] = merged;
                  found = true;
                  break;
                }
              }

              // If not found and it's a created event, insert at the beginning
              if (!found && event.type === "flow_message.created") {
                if (
                  newData.pages.length > 0 &&
                  newData.pages[0]?.data?.flow_messages
                ) {
                  newData.pages[0].data.flow_messages.unshift(updatedMessage);
                } else if (newData.pages.length === 0) {
                  newData.pages.push({
                    data: { flow_messages: [updatedMessage] },
                  });
                }
              }

              return newData;
            },
          );

          {
            const flowSessionId = updatedMessage.flow_session_id;
            const fileRecordIds = updatedMessage.file_record_ids;
            if (flowSessionId && fileRecordIds?.length) {
              const listKey = fileRecordKeys.flowSession(String(flowSessionId));
              const listed = queryClient.getQueryData<FileRecord[]>(listKey);
              const listedIds = new Set(listed?.map((r) => r.id) ?? []);
              if (fileRecordIds.some((id) => !listedIds.has(id))) {
                queryClient.invalidateQueries({ queryKey: listKey });
              }
            }
          }

          break;

        case "flow_message.content_delta":
          const contentDelta = event.data.flow_message_content_delta;

          // Skip processing if this session is not actively viewed
          // This allows faster backpressure response and reduces CPU usage
          if (
            viewedSessionId &&
            contentDelta.flow_session_id !== viewedSessionId
          ) {
            // Don't process - when user navigates to session, normal query fetching will get latest data
            return;
          }

          // Update flow messages cache by appending content delta
          queryClient.setQueryData<InfiniteData<MessagesResponse>>(
            ["flowMessages", contentDelta.flow_session_id],
            (oldData: InfiniteData<MessagesResponse> | undefined) => {
              if (!oldData) return oldData;

              const newData = cloneDeep(oldData);
              let found = false;

              // Search through all pages to find and update the message
              for (const page of newData.pages) {
                const existingMessage = page.data.flow_messages.find(
                  (msg) => msg.id === contentDelta.flow_message_id,
                );

                if (existingMessage) {
                  const disposition = classifyDelta(
                    existingMessage._lastContentDelta,
                    contentDelta.attempt ?? 0,
                    contentDelta.index,
                  );
                  if (disposition !== "drop") {
                    existingMessage.content =
                      disposition === "restart"
                        ? contentDelta.content
                        : existingMessage.content + contentDelta.content;
                    existingMessage._lastContentDelta = {
                      attempt: contentDelta.attempt ?? 0,
                      index: contentDelta.index,
                    };
                  }
                  existingMessage.status = contentDelta.done
                    ? "done"
                    : "streaming";
                  existingMessage.updated_at = new Date().toISOString();
                  found = true;
                  break;
                }
              }

              // If message not found on any page, create optimistic message
              // and add to first page. This must run once after the loop —
              // running it inside the loop would fork a duplicate message
              // (same id) for every page that lacks the message.
              if (!found) {
                const optimisticMessage: Message = {
                  id: contentDelta.flow_message_id,
                  user_id: user?.user.id!,
                  organization_id: user?.organization.id!,
                  flow_session_id: contentDelta.flow_session_id,
                  status: "streaming",
                  origin: "assistant",
                  content: contentDelta.content,
                  _lastContentDelta: {
                    attempt: contentDelta.attempt ?? 0,
                    index: contentDelta.index,
                  },
                  sent_at: new Date().toISOString(),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  tool_calls: [],
                };

                if (
                  newData.pages.length > 0 &&
                  newData.pages[0]?.data?.flow_messages
                ) {
                  newData.pages[0].data.flow_messages.unshift(
                    optimisticMessage,
                  );
                } else if (newData.pages.length === 0) {
                  newData.pages.push({
                    data: { flow_messages: [optimisticMessage] },
                  });
                }
              }

              return newData;
            },
          );
          break;

        case "flow_message.thinking_delta": {
          const thinkingDelta = event.data.flow_message_thinking_delta;

          if (
            viewedSessionId &&
            thinkingDelta.flow_session_id !== viewedSessionId
          ) {
            return;
          }

          queryClient.setQueryData<InfiniteData<MessagesResponse>>(
            ["flowMessages", thinkingDelta.flow_session_id],
            (oldData: InfiniteData<MessagesResponse> | undefined) => {
              if (!oldData) return oldData;

              const newData = cloneDeep(oldData);
              let found = false;

              for (const page of newData.pages) {
                const existingMessage = page.data.flow_messages.find(
                  (msg) => msg.id === thinkingDelta.flow_message_id,
                );
                if (existingMessage) {
                  const disposition = classifyDelta(
                    existingMessage._lastThinkingDelta,
                    thinkingDelta.attempt ?? 0,
                    thinkingDelta.index,
                  );
                  if (disposition !== "drop") {
                    if (
                      existingMessage.thinking_content == null ||
                      disposition === "restart"
                    ) {
                      existingMessage.thinking_content = "";
                    }
                    existingMessage.thinking_content += thinkingDelta.content;
                    existingMessage._lastThinkingDelta = {
                      attempt: thinkingDelta.attempt ?? 0,
                      index: thinkingDelta.index,
                    };
                  }
                  const hasTerminalStatus =
                    existingMessage.status === "done" ||
                    existingMessage.status === "processed" ||
                    existingMessage.status === "failed" ||
                    existingMessage.status === "cancelled";
                  // Thinking completion is not message completion; keep streaming
                  // until we receive terminal message/content events.
                  if (!hasTerminalStatus) {
                    existingMessage.status = "streaming";
                  }
                  existingMessage.updated_at = new Date().toISOString();
                  if (
                    thinkingDelta.done &&
                    thinkingDelta.duration_milliseconds != null
                  ) {
                    existingMessage.thinking_duration_milliseconds =
                      thinkingDelta.duration_milliseconds;
                  }
                  found = true;
                  break;
                }
              }

              if (!found) {
                const optimisticMessage: Message = {
                  id: thinkingDelta.flow_message_id,
                  user_id: thinkingDelta.user_id,
                  organization_id: thinkingDelta.organization_id,
                  flow_session_id: thinkingDelta.flow_session_id,
                  status: "streaming",
                  origin: "assistant",
                  content: "",
                  thinking_content: thinkingDelta.content,
                  _lastThinkingDelta: {
                    attempt: thinkingDelta.attempt ?? 0,
                    index: thinkingDelta.index,
                  },
                  ...(thinkingDelta.done &&
                    thinkingDelta.duration_milliseconds != null && {
                      thinking_duration_milliseconds:
                        thinkingDelta.duration_milliseconds,
                    }),
                  sent_at: new Date().toISOString(),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  tool_calls: [],
                };
                if (
                  newData.pages.length > 0 &&
                  newData.pages[0]?.data?.flow_messages
                ) {
                  newData.pages[0].data.flow_messages.unshift(
                    optimisticMessage,
                  );
                } else if (newData.pages.length === 0) {
                  newData.pages.push({
                    data: { flow_messages: [optimisticMessage] },
                  });
                }
              }

              return newData;
            },
          );
          break;
        }

        case "flow_message.tool_call_args_delta": {
          const argsDeltaData = event.data.flow_message_tool_call_args_delta;

          if (
            viewedSessionId &&
            argsDeltaData.flow_session_id !== viewedSessionId
          ) {
            return;
          }

          const toolCallFromEvent = argsDeltaData.tool_call;
          const placeholderActionDescription = "";

          function buildToolCallWithPlaceholder(): ToolCall {
            return {
              id: toolCallFromEvent.id,
              index: toolCallFromEvent.index ?? 0,
              type: toolCallFromEvent.type ?? "function",
              function: {
                name: toolCallFromEvent.function?.name ?? "",
                execution_tool_name:
                  toolCallFromEvent.function?.execution_tool_name ?? "",
                platform_name: toolCallFromEvent.function?.platform_name ?? "",
                platform_display_name:
                  toolCallFromEvent.function?.platform_display_name,
                platform_image_url:
                  toolCallFromEvent.function?.platform_image_url,
                arguments: {
                  action_description: placeholderActionDescription,
                },
              },
              status: "processing" as const,
            };
          }

          // Append the new chunk and try to extract `action_description`
          // from the partial buffer. Once extracted we mark the tool call
          // as done-parsing — subsequent deltas just no-op until the
          // terminal `flow_message.tool_call` event lands with the full
          // server-parsed args. This is the only field any consumer reads
          // from `function.arguments` during streaming (see ToolCallTitle,
          // useFlowSessionLiveStatus); the rest of the args object is
          // populated wholesale from the terminal event.
          function appendDeltaAndExtractActionDescription(
            toolCall: ToolCall,
            delta: string,
          ): void {
            if (toolCall._argsParsedDone) return;
            toolCall._accumulatedArgs =
              (toolCall._accumulatedArgs ?? "") + delta;
            const parsed = parsePartialJson(toolCall._accumulatedArgs) as
              | { action_description?: unknown }
              | undefined;
            const desc = parsed?.action_description;
            if (typeof desc === "string" && desc.length > 0) {
              toolCall.function.arguments = {
                ...(toolCall.function.arguments ?? {}),
                action_description: desc,
              };
              toolCall._argsParsedDone = true;
              // Buffer no longer needed; the terminal event carries the
              // full server-parsed args.
              delete toolCall._accumulatedArgs;
            }
          }

          queryClient.setQueryData<InfiniteData<MessagesResponse>>(
            ["flowMessages", argsDeltaData.flow_session_id],
            (oldData: InfiniteData<MessagesResponse> | undefined) => {
              if (!oldData) return oldData;

              const newData = cloneDeep(oldData);
              let found = false;

              for (const page of newData.pages) {
                const existingMessage = page.data.flow_messages.find(
                  (msg) => msg.id === argsDeltaData.flow_message_id,
                );
                if (existingMessage) {
                  if (existingMessage.origin !== "assistant-tool-call") {
                    existingMessage.origin = "assistant-tool-call";
                  }
                  if (!existingMessage.tool_calls) {
                    existingMessage.tool_calls = [];
                  }
                  const existingToolCall = existingMessage.tool_calls.find(
                    (tc) => tc.id === toolCallFromEvent.id,
                  );
                  if (existingToolCall) {
                    appendDeltaAndExtractActionDescription(
                      existingToolCall,
                      argsDeltaData.args_delta,
                    );
                  } else {
                    const newToolCall = buildToolCallWithPlaceholder();
                    appendDeltaAndExtractActionDescription(
                      newToolCall,
                      argsDeltaData.args_delta,
                    );
                    existingMessage.tool_calls.push(newToolCall);
                  }
                  const hasTerminalStatus =
                    existingMessage.status === "done" ||
                    existingMessage.status === "processed" ||
                    existingMessage.status === "failed" ||
                    existingMessage.status === "cancelled";
                  if (!hasTerminalStatus) {
                    existingMessage.status = "streaming";
                  }
                  existingMessage.updated_at = new Date().toISOString();
                  found = true;
                  break;
                }
              }

              if (!found) {
                const newToolCall = buildToolCallWithPlaceholder();
                appendDeltaAndExtractActionDescription(
                  newToolCall,
                  argsDeltaData.args_delta,
                );
                const optimisticMessage: Message = {
                  id: argsDeltaData.flow_message_id,
                  user_id: argsDeltaData.user_id,
                  organization_id: argsDeltaData.organization_id,
                  flow_session_id: argsDeltaData.flow_session_id,
                  status: "streaming",
                  origin: "assistant-tool-call",
                  content: "",
                  sent_at: new Date().toISOString(),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  tool_calls: [newToolCall],
                };
                if (
                  newData.pages.length > 0 &&
                  newData.pages[0]?.data?.flow_messages
                ) {
                  newData.pages[0].data.flow_messages.unshift(
                    optimisticMessage,
                  );
                } else if (newData.pages.length === 0) {
                  newData.pages.push({
                    data: { flow_messages: [optimisticMessage] },
                  });
                }
              }

              return newData;
            },
          );
          break;
        }

        case "flow_message.tool_call":
          const toolCallData = event.data.flow_message_tool_call;

          toolCallData.tool_call.status = "processing";

          // Update flow messages cache to add tool call to existing message or create optimistic message
          queryClient.setQueryData<InfiniteData<MessagesResponse>>(
            ["flowMessages", toolCallData.flow_session_id],
            (oldData: InfiniteData<MessagesResponse> | undefined) => {
              if (!oldData) {
                // If no data exists, create initial structure with optimistic message
                return {
                  pages: [
                    {
                      data: {
                        flow_messages: [
                          {
                            id: toolCallData.flow_message_id,
                            user_id: toolCallData.user_id,
                            organization_id: toolCallData.organization_id,
                            flow_session_id: toolCallData.flow_session_id,
                            status: "streaming",
                            origin: "assistant",
                            content: "",
                            sent_at: new Date().toISOString(),
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            tool_calls: [toolCallData.tool_call],
                          },
                        ],
                      },
                    },
                  ],
                  pageParams: [undefined],
                };
              }

              const newData = cloneDeep(oldData);
              let messageFound = false;

              // Search through all pages to find and update the message
              for (const page of newData.pages) {
                const existingMessage = page.data.flow_messages.find(
                  (msg) => msg.id === toolCallData.flow_message_id,
                );
                if (existingMessage) {
                  // Message found - add or update tool call (avoid duplicate if already added by tool_call_args_delta)
                  if (!existingMessage.tool_calls) {
                    existingMessage.tool_calls = [];
                  }
                  const existingById = existingMessage.tool_calls.find(
                    (tc) => tc.id === toolCallData.tool_call.id,
                  );
                  if (existingById) {
                    // Already present (e.g. from tool_call_args_delta): merge in full payload (status, user_confirmation_status, etc.)
                    Object.assign(existingById, toolCallData.tool_call);
                    // Server-parsed args supersede the streaming fast-path
                    // state — drop the buffer and done-flag.
                    delete existingById._accumulatedArgs;
                    delete existingById._argsParsedDone;
                  } else {
                    existingMessage.tool_calls.push(toolCallData.tool_call);
                  }
                  existingMessage.updated_at = new Date().toISOString();
                  messageFound = true;
                  break;
                }
              }

              // If message not found, create optimistic message and add to first page
              if (!messageFound) {
                const optimisticMessage: Message = {
                  id: toolCallData.flow_message_id,
                  user_id: toolCallData.user_id,
                  organization_id: toolCallData.organization_id,
                  flow_session_id: toolCallData.flow_session_id,
                  status: "streaming",
                  origin: "assistant",
                  content: "",
                  sent_at: new Date().toISOString(),
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  tool_calls: [toolCallData.tool_call],
                };

                if (
                  newData.pages.length > 0 &&
                  newData.pages[0]?.data?.flow_messages
                ) {
                  newData.pages[0].data.flow_messages.unshift(
                    optimisticMessage,
                  );
                } else if (newData.pages.length === 0) {
                  newData.pages.push({
                    data: { flow_messages: [optimisticMessage] },
                  });
                }
              }

              return newData;
            },
          );
          break;

        case "inbox_item.created":
        case "inbox_item.updated":
          queryClient.invalidateQueries({
            queryKey: ["inboxItemsInfinite"],
          });

          queryClient.invalidateQueries({
            queryKey: ["inboxItems"],
          });

          // insert or update inbox item in cache
          if (event.data.inbox_item.id) {
            queryClient.setQueryData<InboxItem>(
              ["inboxItem", event.data.inbox_item.id],
              (prev) => ({
                ...prev,
                ...event.data.inbox_item,
              }),
            );

            queryClient.setQueryData<InboxItem[]>(["inboxItems"], (prev) =>
              prev?.map((item) =>
                item.id === event.data.inbox_item.id
                  ? {
                      ...item,
                      ...event.data.inbox_item,
                    }
                  : item,
              ),
            );
          }

          break;

        case "canvas.updated": {
          const canvas = event.data.canvas;
          if (!canvas?.id) {
            break;
          }
          const canvasId = String(canvas.id);

          // Moving to trash arrives as canvas.updated with archived_at set.
          // Mirror canvas.deleted: drop the detail cache so a navigated-in view
          // refetches the in-trash banner, and refresh lists + trash.
          if (canvas.archived_at) {
            queryClient.removeQueries({ queryKey: ["canvas", canvasId] });
            queryClient.invalidateQueries({ queryKey: ["canvases"] });
            queryClient.invalidateQueries({ queryKey: ["canvas"] });
            queryClient.invalidateQueries({ queryKey: ["workspaces"] });
            queryClient.invalidateQueries({ queryKey: ["workspace"] });
            queryClient.invalidateQueries({ queryKey: ["archived"] });
            break;
          }
          // Always merge into an existing detail entry. The event payload is a
          // slim projection (no liveblocks_room_id), so seeding an empty cache
          // with it would make the canvas page render "Canvas unavailable"
          // until a real refetch — seen after restoring a canvas from Trash,
          // where canvas.deleted removed the entry and canvas.updated re-seeded
          // it with the partial event shape.
          //
          // Do this *before* the list-membership gate: clients with the canvas
          // open (or Share dialog mounted) hold `["canvas", id]` even when the
          // exact `["canvases"]` list key is empty (real lists are
          // `["canvases", "all"]` / `["canvases", "recent", …]`). Skipping the
          // merge left publish status stuck on optimistic "publishing".
          const hadDetail = !!queryClient.getQueryData<Canvas>([
            "canvas",
            canvasId,
          ]);
          queryClient.setQueryData<Canvas>(["canvas", canvasId], (old) =>
            old ? mergeCanvasQueryEntry(old, canvas) : old,
          );

          // Also patch any cached canvas list that contains this id (prefix
          // match — exact `["canvases"]` is rarely populated).
          for (const query of queryClient.getQueriesData<Canvas[]>({
            queryKey: ["canvases"],
          })) {
            const [key, list] = query;
            if (!Array.isArray(list)) continue;
            if (!list.some((c) => String(c.id) === canvasId)) continue;
            queryClient.setQueryData<Canvas[]>(key, (old) => {
              if (!old) return old;
              return old.map((c) =>
                String(c.id) === canvasId
                  ? mergeCanvasQueryEntry(c, canvas)
                  : c,
              );
            });
          }

          // Restore (archived_at cleared) reaches other clients as a plain
          // canvas.updated — the canvas was dropped from the list when it was
          // archived, so if it's absent from every cached list *and* we have
          // no detail entry, treat this as a re-appearance and refresh like
          // canvas.created. Gated so org-wide broadcasts don't refetch for
          // uninvolved clients.
          const inAnyList = queryClient
            .getQueriesData<Canvas[]>({ queryKey: ["canvases"] })
            .some(([, list]) =>
              Array.isArray(list)
                ? list.some((c) => String(c.id) === canvasId)
                : false,
            );
          if (!hadDetail && !inAnyList) {
            queryClient.invalidateQueries({ queryKey: ["canvases"] });
            queryClient.invalidateQueries({ queryKey: ["canvas"] });
            queryClient.invalidateQueries({ queryKey: ["workspaces"] });
            queryClient.invalidateQueries({ queryKey: ["workspace"] });
            queryClient.invalidateQueries({ queryKey: ["archived"] });
          }
          break;
        }

        case "canvas.created": {
          // Lists are keyed many different ways (workspace-scoped, recent,
          // shared-with-me, by-workspace map). Match the create mutation's
          // invalidation surface rather than trying to splice the new row
          // into every cache.
          if (!event.data.canvas?.id) break;
          queryClient.invalidateQueries({ queryKey: ["canvases"] });
          queryClient.invalidateQueries({ queryKey: ["canvas"] });
          queryClient.invalidateQueries({ queryKey: ["workspaces"] });
          queryClient.invalidateQueries({ queryKey: ["workspace"] });
          break;
        }

        case "canvas.deleted": {
          const canvasId = event.data.canvas?.id;
          if (!canvasId) break;
          // Drop the detail cache so any view navigated into the now-trashed
          // canvas refetches and renders the in-trash banner instead of stale data.
          queryClient.removeQueries({ queryKey: ["canvas", String(canvasId)] });
          queryClient.invalidateQueries({ queryKey: ["canvases"] });
          queryClient.invalidateQueries({ queryKey: ["canvas"] });
          queryClient.invalidateQueries({ queryKey: ["workspaces"] });
          queryClient.invalidateQueries({ queryKey: ["workspace"] });
          queryClient.invalidateQueries({ queryKey: ["archived"] });
          break;
        }

        case "flow_session.deleted": {
          const flowSessionId = event.data.flow_session?.id;
          if (!flowSessionId) break;
          queryClient.removeQueries({
            queryKey: ["flowSession", String(flowSessionId)],
          });
          queryClient.invalidateQueries({
            queryKey: ["workspace-flow-sessions"],
          });
          queryClient.invalidateQueries({ queryKey: ["canvas-flow-sessions"] });
          queryClient.invalidateQueries({ queryKey: ["archived"] });
          break;
        }

        case "billing_subscription.updated": {
          // Drop the routing-only organization_id before merging so the cached
          // MeData.subscription stays the shape /me would have returned.
          const { organization_id: _orgId, ...subscription } =
            event.data.billing_subscription;
          queryClient.setQueryData<MeData>(["userInfo"], (prev) => {
            // Don't seed an empty cache from a partial event — /me hydrates
            // it on auth and carries fields (permissions, organizations)
            // the event doesn't.
            if (!prev) return prev;
            return {
              ...prev,
              subscription: { ...prev.subscription, ...subscription },
            };
          });
          // Subscription changes can flip usage_allowed without a
          // billing_usage.updated event firing — a new period resets credit
          // counters, and an overage-limit raise can re-enable spending.
          // Invalidate so the usage portion reconciles with the server.
          queryClient.invalidateQueries({ queryKey: ["userInfo"] });
          // The detail-billing screens read a fuller StripeSubscription from
          // a separate endpoint that includes fields the event payload omits
          // (billing_email, max_period_*); refetch on update.
          queryClient.invalidateQueries({ queryKey: ["subscription"] });
          break;
        }

        case "billing_usage.updated": {
          const {
            organization_id: _orgId,
            user_id: _userId,
            ...usage
          } = event.data.billing_usage;
          queryClient.setQueryData<MeData>(["userInfo"], (prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              usage: { ...prev.usage, ...usage },
            };
          });
          break;
        }

        case "file_record.created":
        case "file_record.updated":
        case "file_record.deleted": {
          // Apply the event payload directly to the cache instead of
          // invalidating — invalidation triggers a network refetch that
          // adds 100-300ms of latency on every event, which makes
          // pending → processed transitions visibly lag. The event
          // already carries the full FileRecord, so we can drive the UI
          // off it synchronously.
          const fileRecord = event.data.file_record;
          if (!fileRecord) break;

          const isDelete = event.type === "file_record.deleted";

          // Per-id detail cache: kept live for every class so consumers
          // of `useFileRecordsByIds` (chat messages, upload queues,
          // detail panes) settle without polling regardless of where
          // the record lives.
          const detailKey = fileRecordKeys.detail(fileRecord.id);
          if (isDelete) {
            queryClient.removeQueries({ queryKey: detailKey });
          } else {
            queryClient.setQueryData<FileRecord>(detailKey, (prev) =>
              prev
                ? { ...prev, ...(fileRecord as FileRecord) }
                : (fileRecord as FileRecord),
            );
          }

          // Version history is derived from S3 events server-side; the WS
          // payload does not include `file_record_versions`, so refresh the
          // list used by the detail-pane aside when content changes.
          if (
            event.type === "file_record.created" ||
            event.type === "file_record.updated"
          ) {
            queryClient.invalidateQueries({
              queryKey: fileRecordKeys.versions(fileRecord.id),
            });
          }

          /** Produce the next cache state for a single class-scoped file
           *  list given an event-carried record. Returns `prev`
           *  unchanged when the event doesn't affect this cache. */
          const applyToCache = (
            prev: FileRecord[] | undefined,
          ): FileRecord[] | undefined => {
            // Don't seed an empty cache — list queries hydrate on mount.
            // If the user hasn't opened this workspace yet there's
            // nothing to render, and we'd rather not race the initial
            // fetch.
            if (!prev) return prev;

            if (isDelete) {
              const filtered = prev.filter((r) => r.id !== fileRecord.id);
              return filtered.length === prev.length ? prev : filtered;
            }

            const idx = prev.findIndex((r) => r.id === fileRecord.id);
            if (idx === -1) {
              return [...prev, fileRecord as FileRecord];
            }

            // Spread-merge so source_file_record_status / _content_hash
            // (computed by the list endpoint, absent from the event
            // payload) survive an update event.
            const next = prev.slice();
            next[idx] = { ...prev[idx], ...(fileRecord as FileRecord) };
            return next;
          };

          // Home Recents spans every class and is creator-scoped + ordered
          // server-side, so membership of a NEW record is the server's call:
          // creates invalidate (one refetch per upload — rare next to status
          // events), while updates/deletes patch in place / remove so the
          // latency-sensitive pending → processed transitions stay
          // network-free. Without this, composer uploads and processing
          // updates never reach the home All/Files tabs until an unrelated
          // refetch.
          if (event.type === "file_record.created") {
            queryClient.invalidateQueries({
              queryKey: fileRecordKeys.recent(),
            });
          } else {
            queryClient.setQueriesData<FileRecord[]>(
              { queryKey: fileRecordKeys.recent() },
              (prev) => {
                if (!prev) return prev;
                if (isDelete) {
                  const filtered = prev.filter((r) => r.id !== fileRecord.id);
                  return filtered.length === prev.length ? prev : filtered;
                }
                const idx = prev.findIndex((r) => r.id === fileRecord.id);
                if (idx === -1) return prev;
                const next = prev.slice();
                next[idx] = { ...prev[idx], ...(fileRecord as FileRecord) };
                return next;
              },
            );
          }

          // "Shared with me" spans every class. Membership is share-driven, so
          // patch/remove an existing entry but never append (a workspace/canvas
          // event must not inject a file the user wasn't explicitly shared).
          queryClient.setQueryData<FileRecord[]>(
            fileRecordKeys.sharedWithMe(),
            (prev) => {
              if (!prev) return prev;
              if (isDelete) {
                const filtered = prev.filter((r) => r.id !== fileRecord.id);
                return filtered.length === prev.length ? prev : filtered;
              }
              const idx = prev.findIndex((r) => r.id === fileRecord.id);
              if (idx === -1) return prev;
              const next = prev.slice();
              next[idx] = { ...prev[idx], ...(fileRecord as FileRecord) };
              return next;
            },
          );

          if (fileRecord.class === "canvas") {
            const canvasId = fileRecord.canvas_id;
            if (canvasId) {
              queryClient.setQueryData<FileRecord[]>(
                fileRecordKeys.canvas(String(canvasId)),
                applyToCache,
              );
              break;
            }

            const caches = queryClient.getQueriesData<FileRecord[]>({
              queryKey: ["fileRecords", "canvas"],
            });
            let matched = false;
            for (const [key, data] of caches) {
              if (!data) continue;
              if (!data.some((r) => r.id === fileRecord.id)) continue;
              matched = true;
              queryClient.setQueryData<FileRecord[]>(key, applyToCache);
            }
            if (!matched && !isDelete) {
              queryClient.invalidateQueries({
                queryKey: ["fileRecords", "canvas"],
              });
            }
            break;
          }

          if (fileRecord.class === "flow-session") {
            const flowSessionId = fileRecord.flow_session_id;
            if (flowSessionId) {
              queryClient.setQueryData<FileRecord[]>(
                fileRecordKeys.flowSession(String(flowSessionId)),
                applyToCache,
              );
              break;
            }

            const caches = queryClient.getQueriesData<FileRecord[]>({
              queryKey: ["fileRecords", "flowSession"],
            });
            let matched = false;
            for (const [key, data] of caches) {
              if (!data) continue;
              if (!data.some((r) => r.id === fileRecord.id)) continue;
              matched = true;
              queryClient.setQueryData<FileRecord[]>(key, applyToCache);
            }
            if (!matched && !isDelete) {
              queryClient.invalidateQueries({
                queryKey: ["fileRecords", "flowSession"],
              });
            }
            break;
          }

          if (fileRecord.class !== "workspace") break;

          const workspaceId = fileRecord.workspace_id;
          if (workspaceId) {
            queryClient.setQueryData<FileRecord[]>(
              fileRecordKeys.workspace(String(workspaceId)),
              applyToCache,
            );
            break;
          }

          // Fallback: `workspace_id` is declared optional on the wire
          // (Go pointer with `omitempty`). If it's ever missing we
          // still don't want to silently drop the event — scan every
          // loaded workspace cache, update the one holding this id for
          // updates/deletes, and fall through to invalidation for
          // creates (we can't know which cache the new record belongs
          // in without the id).
          const caches = queryClient.getQueriesData<FileRecord[]>({
            queryKey: ["fileRecords", "workspace"],
          });
          let matched = false;
          for (const [key, data] of caches) {
            if (!data) continue;
            if (!data.some((r) => r.id === fileRecord.id)) continue;
            matched = true;
            queryClient.setQueryData<FileRecord[]>(key, applyToCache);
          }
          if (!matched && !isDelete) {
            // Unknown destination for a create — ask any mounted
            // workspace queries to refetch. Worst case: a single
            // refetch per open workspace tab; far better than
            // perpetual staleness.
            queryClient.invalidateQueries({
              queryKey: ["fileRecords", "workspace"],
            });
          }
          break;
        }
        case "skill.created": {
          const skill = event.data.skill;
          if (!skill?.id) break;
          queryClient.invalidateQueries({ queryKey: ["skills"] });
          break;
        }
        case "skill.updated": {
          const skill = event.data.skill;
          if (!skill?.id) break;
          const skillId = String(skill.id);
          // Moving to trash: archived_at set — close detail, drop from lists.
          if (skill.archived_at) {
            queryClient.removeQueries({ queryKey: ["skill", skillId] });
            queryClient.invalidateQueries({ queryKey: ["skills"] });
            queryClient.invalidateQueries({ queryKey: ["archived"] });
            break;
          }
          // Restore (archived_at cleared) or a routine edit: refresh detail + list
          // (and the archive, in case this was a restore).
          queryClient.invalidateQueries({ queryKey: ["skill", skillId] });
          queryClient.invalidateQueries({ queryKey: ["skills"] });
          queryClient.invalidateQueries({ queryKey: ["archived"] });
          break;
        }
        case "skill.deleted": {
          const skill = event.data.skill;
          if (!skill?.id) break;
          queryClient.removeQueries({ queryKey: ["skill", String(skill.id)] });
          queryClient.invalidateQueries({ queryKey: ["skills"] });
          queryClient.invalidateQueries({ queryKey: ["archived"] });
          break;
        }
        default:
          // Handle unknown event types gracefully
          console.warn("Unknown event type received:", (event as any).type);
          break;
      }
    },
    [queryClient, location.pathname],
  );
}
